import React, { Fragment, useState } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { ErrorComponent, JwlTable, LoadingSpinner } from '../../../../components';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import { ProducerNavigation } from '../../navigation/ProducerNavigation';

interface IProps {
  party: GQLParty;
}

const SymbolCollectionsList: React.FC<IProps> = ({ party }) => {
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [filteredSymbolCollections, setFilteredSymbolCollections] = useState('');
  const history = useHistory();

  const {
    loading: symbolCollectionLoading,
    error: symbolCollectionError,
    data: symbolCollectionData,
    refetch,
    networkStatus: symbolCollectionNetworkStatus
  } = GQLOperationBuilder.symbolCollection.getSymbolCollectionsByProducer({ producerPartyId: party.id });

  if (symbolCollectionLoading || symbolCollectionNetworkStatus === NetworkStatus.refetch)
    return <LoadingSpinner />;

  if (symbolCollectionError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={symbolCollectionError?.stack}
        message={symbolCollectionError.message}
        callback={() => refetch()}
      />
    );

  const handleFilterChange = (event: any) => setFilteredSymbolCollections(event.target.value);

  const getSymbolCollectionRows = () => {
    return GQLOperationBuilder.symbolCollection
      .symbolCollectionArrayFromResponse(symbolCollectionData)
      .filter(symbolCollection => {
        return (
          symbolCollection.title.en!.toLowerCase().includes(filteredSymbolCollections.toLowerCase().trim()) ||
          symbolCollection.description
            .en!.toLowerCase()
            .includes(filteredSymbolCollections.toLowerCase().trim())
        );
      })
      .sort((s1, s2) => s1.title.en!.localeCompare(s2.title.en!))
      .map(symbolCollection => {
        return {
          id: symbolCollection.id,
          cells: [symbolCollection.title.en, symbolCollection.description.en, symbolCollection.symbols.length]
        };
      });
  };

  const navigateToSymbolCollectionDetails = (symbolCollectionId: string) => {
    history.push(
      ProducerNavigation.symbolCollectionDetails.pathFromParams({ symbolCollectionId: symbolCollectionId })
    );
  };

  return (
    <Fragment>
      <JwlTable
        title="Producer symbol collections list"
        description={`The list of all current symbol collections available for the producer ${party.name}.`}
        columns={['Collection name', 'Collection description', 'Collection symbols']}
        rows={getSymbolCollectionRows()}
        onFilterChange={handleFilterChange}
        onTableRowClick={navigateToSymbolCollectionDetails}
      />
    </Fragment>
  );
};

export default SymbolCollectionsList;
